// client/src/services/missionTypeService.ts
import { apiClient } from './api';

// Custom field types
export interface CustomFieldOption {
  label: string;
  value: string | number;
}

export interface CustomFieldValidation {
  min?: number;
  max?: number;
}

export interface CustomFieldConfig {
  name: string;
  label: string;
  type: 'text' | 'number' | 'select' | 'checkbox' | 'textarea' | 'date';
  required: boolean;
  placeholder?: string;
  options?: CustomFieldOption[];
  validation?: CustomFieldValidation;
}

// Interfaces pour les types de missions
export interface MissionType {
  id: string;
  name: string;
  description: string;
  color: string;
  base_price: number;
  price_per_km: number;
  estimated_duration: number; // en minutes
  requires_special_vehicle: boolean;
  vehicle_types: string[];
  custom_fields: CustomFieldConfig[];
  is_active: boolean;
  created_at: string;
  updated_at: string;
  created_by?: string;
  updated_by?: string;
}

export interface MissionTypeFilters {
  page?: number;
  limit?: number;
  search?: string;
  is_active?: boolean;
  sort_by?: 'name' | 'created_at' | 'updated_at' | 'base_price' | 'estimated_duration';
  sort_order?: 'ASC' | 'DESC';
}

export interface MissionTypeFormData {
  name: string;
  description?: string;
  color?: string;
  base_price: number;
  price_per_km: number;
  estimated_duration: number;
  requires_special_vehicle?: boolean;
  vehicle_types: string[];
  custom_fields?: CustomFieldConfig[];
  is_active?: boolean;
}

export interface MissionTypeStats {
  total: number;
  active: number;
  inactive: number;
  avg_base_price: number;
  avg_price_per_km: number;
  avg_duration: number;
  most_used: Array<{
    id: string;
    name: string;
    color: string;
    usage_count: number;
  }>;
}

export interface MissionTypesResponse {
  data: {
    mission_types: MissionType[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
      hasNext: boolean;
      hasPrev: boolean;
    };
  };
  message: string;
  timestamp: string;
}

/**
 * Service pour la gestion des types de missions
 * Gère toutes les interactions avec l'API des types de missions
 */
class MissionTypeService {
  private baseUrl = '/admin/mission-types';

  /**
   * Récupérer la liste des types de missions avec filtres et pagination
   */
  async getMissionTypes(filters: MissionTypeFilters = {}): Promise<MissionTypesResponse> {
    const url = apiClient.buildUrl(this.baseUrl, filters);
    return apiClient.get<MissionTypesResponse>(url);
  }

  /**
   * Récupérer tous les types de missions actifs (pour les sélecteurs)
   */
  async getActiveMissionTypes(): Promise<MissionType[]> {
    const response = await this.getMissionTypes({ 
      is_active: true, 
      limit: 100,
      sort_by: 'name',
      sort_order: 'ASC'
    });
    return response.data.mission_types;
  }

  /**
   * Récupérer un type de mission par ID
   */
  async getMissionType(id: string): Promise<MissionType> {
    return apiClient.get<MissionType>(`${this.baseUrl}/${id}`);
  }

  /**
   * Créer un nouveau type de mission
   */
  async createMissionType(data: MissionTypeFormData): Promise<MissionType> {
    return apiClient.post<MissionType>(this.baseUrl, data);
  }

  /**
   * Mettre à jour un type de mission
   */
  async updateMissionType(id: string, data: Partial<MissionTypeFormData>): Promise<MissionType> {
    return apiClient.put<MissionType>(`${this.baseUrl}/${id}`, data);
  }

  /**
   * Supprimer un type de mission
   */
  async deleteMissionType(id: string): Promise<void> {
    return apiClient.delete(`${this.baseUrl}/${id}`);
  }

  /**
   * Activer/désactiver un type de mission
   */
  async toggleMissionTypeStatus(id: string): Promise<MissionType> {
    return apiClient.patch<MissionType>(`${this.baseUrl}/${id}/toggle-status`);
  }

  /**
   * Dupliquer un type de mission
   */
  async duplicateMissionType(
    id: string, 
    modifications?: Partial<MissionTypeFormData>
  ): Promise<MissionType> {
    return apiClient.post<MissionType>(`${this.baseUrl}/${id}/duplicate`, {
      modifications: modifications || {}
    });
  }

  /**
   * Récupérer les statistiques des types de missions
   */
  async getMissionTypeStats(): Promise<MissionTypeStats> {
    const response = await apiClient.get<{ data: MissionTypeStats }>(`${this.baseUrl}/stats`);
    return response.data;
  }

  /**
   * Rechercher des types de missions
   */
  async searchMissionTypes(searchTerm: string): Promise<MissionType[]> {
    const response = await this.getMissionTypes({ 
      search: searchTerm,
      limit: 20,
      sort_by: 'name',
      sort_order: 'ASC'
    });
    return response.data.mission_types;
  }

  /**
   * Validation côté client pour les données d'un type de mission
   */
  validateMissionTypeData(data: Partial<MissionTypeFormData>): string[] {
    const errors: string[] = [];

    if (data.name !== undefined) {
      if (!data.name || data.name.trim().length === 0) {
        errors.push('Le nom est requis');
      } else if (data.name.length > 100) {
        errors.push('Le nom ne peut pas dépasser 100 caractères');
      }
    }

    if (data.description !== undefined && data.description.length > 1000) {
      errors.push('La description ne peut pas dépasser 1000 caractères');
    }

    if (data.color !== undefined) {
      const colorRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;
      if (!colorRegex.test(data.color)) {
        errors.push('La couleur doit être un code hexadécimal valide');
      }
    }

    if (data.base_price !== undefined && data.base_price < 0) {
      errors.push('Le prix de base ne peut pas être négatif');
    }

    if (data.price_per_km !== undefined && data.price_per_km < 0) {
      errors.push('Le prix par km ne peut pas être négatif');
    }

    if (data.estimated_duration !== undefined && data.estimated_duration <= 0) {
      errors.push('La durée estimée doit être positive');
    }

    if (data.vehicle_types !== undefined) {
      if (!Array.isArray(data.vehicle_types) || data.vehicle_types.length === 0) {
        errors.push('Au moins un type de véhicule est requis');
      }
    }

    return errors;
  }

  /**
   * Formater la durée en heures et minutes
   */
  formatDuration(minutes: number): string {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    
    if (hours > 0) {
      return mins > 0 ? `${hours}h ${mins}min` : `${hours}h`;
    }
    return `${mins}min`;
  }

  /**
   * Calculer le prix estimé pour une distance donnée
   */
  calculateEstimatedPrice(missionType: MissionType, distanceKm: number): number {
    return missionType.base_price + (missionType.price_per_km * distanceKm);
  }

  /**
   * Obtenir la couleur par défaut pour un nouveau type de mission
   */
  getDefaultColor(): string {
    const colors = [
      '#1890ff', '#52c41a', '#faad14', '#f5222d', 
      '#722ed1', '#eb2f96', '#fa541c', '#13c2c2'
    ];
    return colors[Math.floor(Math.random() * colors.length)];
  }

  /**
   * Obtenir les options de types de véhicules disponibles
   */
  getVehicleTypeOptions(): Array<{ label: string; value: string }> {
    return [
      { label: 'Voiture', value: 'voiture' },
      { label: 'Camionnette', value: 'camionnette' },
      { label: 'Camion', value: 'camion' },
      { label: 'Camion frigorifique', value: 'camion_frigo' },
      { label: 'Camion de déménagement', value: 'camion_demenagement' },
      { label: 'Poids lourd', value: 'poids_lourd' },
      { label: 'Moto', value: 'moto' },
      { label: 'Vélo', value: 'velo' }
    ];
  }

  /**
   * Obtenir le libellé d'un type de véhicule
   */
  getVehicleTypeLabel(value: string): string {
    const option = this.getVehicleTypeOptions().find(opt => opt.value === value);
    return option ? option.label : value;
  }

}

// Instance singleton du service
export const missionTypeService = new MissionTypeService();
export default missionTypeService;